/**
 * The capability catalogue and the deployments that serve it: what each
 * capability is, which deployments exist, and the promote, observe, retire
 * and limits verbs over them, beside the request budgets the dialling verbs
 * are bounded by.
 */

import type {
  DeploymentFilters,
  DeploymentLimitsUpdate,
  DeploymentPromotion,
  DeploymentRetireRequest,
  ModelCapability,
  ModelCapabilityListResponse,
  ModelDeployment,
  ModelDeploymentListResponse,
  PromotionResponse,
  RequestBudgetListResponse,
} from '@/types/modelCapabilities'
import { apiClient, serverBudgetTimeout } from './client'
import { ifMatchHeaders } from './ifMatch'
import { BASE } from './modelCapabilityPaths'

export const modelCapabilityDeployments = {
  // Read before any of the four dialling verbs is called, and the reason
  // their signatures demand a budget: the console waits as long as the server
  // says it may take, never as long as a constant here guessed.
  listRequestBudgets: async (): Promise<RequestBudgetListResponse> => {
    const response = await apiClient.get<RequestBudgetListResponse>(
      `${BASE}/request-budgets/`
    )
    return response.data
  },

  listCapabilities: async (): Promise<ModelCapabilityListResponse> => {
    const response = await apiClient.get<ModelCapabilityListResponse>(
      `${BASE}/capabilities/`
    )
    return response.data
  },

  getCapability: async (key: string): Promise<ModelCapability> => {
    const response = await apiClient.get<ModelCapability>(
      `${BASE}/capabilities/${key}/`
    )
    return response.data
  },

  listDeployments: async (
    filters: DeploymentFilters = {}
  ): Promise<ModelDeploymentListResponse> => {
    const response = await apiClient.get<ModelDeploymentListResponse>(
      `${BASE}/deployments/`,
      { params: filters }
    )
    return response.data
  },

  getDeployment: async (deploymentId: string): Promise<ModelDeployment> => {
    const response = await apiClient.get<ModelDeployment>(
      `${BASE}/deployments/${deploymentId}/`
    )
    return response.data
  },

  // 201 — the reply carries the catalogue row as well as the serving, because
  // the promotion may have created it and a console that re-read the catalogue
  // to find out would be rendering a result it cannot prove came from here.
  promoteDeployment: async (
    body: DeploymentPromotion,
    budgetSeconds: number
  ): Promise<PromotionResponse> => {
    const response = await apiClient.post<PromotionResponse>(
      `${BASE}/deployments/promote/`,
      body,
      serverBudgetTimeout(budgetSeconds)
    )
    return response.data
  },

  // No If-Match: this records what the endpoint says about itself right now,
  // so there is no stated value a concurrent edit could be racing over.
  observeDeployment: async (
    deploymentId: string,
    budgetSeconds: number
  ): Promise<ModelDeployment> => {
    const response = await apiClient.post<ModelDeployment>(
      `${BASE}/deployments/${deploymentId}/observe/`,
      undefined,
      serverBudgetTimeout(budgetSeconds)
    )
    return response.data
  },

  retireDeployment: async (
    deploymentId: string,
    body: DeploymentRetireRequest,
    ifMatch: number
  ): Promise<ModelDeployment> => {
    const response = await apiClient.post<ModelDeployment>(
      `${BASE}/deployments/${deploymentId}/retire/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  // Both limits travel on every save: the verb replaces the declaration whole.
  amendDeploymentLimits: async (
    deploymentId: string,
    body: DeploymentLimitsUpdate,
    ifMatch: number
  ): Promise<ModelDeployment> => {
    const response = await apiClient.put<ModelDeployment>(
      `${BASE}/deployments/${deploymentId}/limits/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },
}
